import type { CheckoutApi } from "../api/types";
import { ApiError } from "../api/types";
import { safeStorage } from "../lib/storage";
import { DEFAULT_CONFIG, parsePublicConfig, type CheckoutConfig } from "./config";
import { brandCss } from "./tokens";

/** Matches the server's own cache lifetime for /public/config. */
export const CONFIG_FRESH_MS = 30_000;
/** A cached config older than this is not used even as a first paint. */
export const CONFIG_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const CONFIG_KEY = "config";
const CSS_KEY = "brandCss";

/**
 * Fetch and parse /public/config.
 *  - config  → the operator's settings
 *  - 404     → an older server without the endpoint: documented defaults
 *  - anything else (offline, timeout, 5xx) → null, so the caller keeps what it has
 */
export async function fetchConfig(api: CheckoutApi, timeoutMs: number): Promise<CheckoutConfig | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return parsePublicConfig(await api.getPublicConfig(ctrl.signal));
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.code === "not_found")) return DEFAULT_CONFIG;
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export function readCachedConfig(now = Date.now()): { config: CheckoutConfig; savedAt: number } | null {
  const raw = safeStorage.get(CONFIG_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { savedAt?: unknown; config?: unknown };
    const savedAt = typeof parsed.savedAt === "number" ? parsed.savedAt : 0;
    if (now - savedAt > CONFIG_MAX_AGE_MS || savedAt > now + 60_000) return null;
    // Re-validated on every read: storage is not trusted any more than the network.
    return { config: parsePublicConfig(parsed.config), savedAt };
  } catch {
    return null;
  }
}

/** The CSS for a config, or null when the built-in palette applies (no operator config). */
export function cssFor(config: CheckoutConfig): string | null {
  if (config.source !== "server") return null;
  try {
    return brandCss(config.brand.primaryColor, config.brand.accentColor);
  } catch {
    return null;
  }
}

/** Remember the config so the next visit paints branded straight away (index.html reads CSS_KEY). */
export function persistConfig(config: CheckoutConfig, now = Date.now()): void {
  if (config.source !== "server") {
    safeStorage.remove(CONFIG_KEY);
    safeStorage.remove(CSS_KEY);
    return;
  }
  safeStorage.set(CONFIG_KEY, JSON.stringify({ savedAt: now, config }));
  const css = cssFor(config);
  if (css) safeStorage.set(CSS_KEY, css);
  else safeStorage.remove(CSS_KEY);
}
